import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { findGeoJsonOutputFormat } from '../../layers/wfs/parseWfsCapabilities.ts';
import { isIntrospectableLayerUrl } from '../../metadata/fetchLayerMetadata.ts';
import { useWfsCapabilities } from './useWfsCapabilities.ts';

export interface IMapLayerWfsConfigInputProps {
  prefix: string;
}

const DETECTED_INFO_STYLE = { display: 'block', marginBottom: 16 };

/**
 * Configurazione WFS di un layer: scelta del feature type (typeName)
 * dalla lista esposta dal GetCapabilities. Se il servizio non risponde
 * (o non espone feature type) il layer non è configurabile: niente
 * inserimento manuale, solo un messaggio bloccante. Il campo `typeName`
 * resta `required`, quindi senza feature type il layer non è salvabile.
 *
 * Versione, outputFormat e CRS nativo vengono rilevati dalle capabilities
 * e persistiti in config: a runtime la mappa usa i valori salvati senza
 * chiamate aggiuntive.
 */
export function MapLayerWfsConfigInput(props: IMapLayerWfsConfigInputProps) {
  const { prefix } = props;
  const setValue = useFormSetValue();

  const url: string | undefined = useWatch({ name: `${prefix}url` });
  const typeName: string | undefined = useWatch({
    name: `${prefix}wfs.typeName`,
  });
  const version: string | undefined = useWatch({
    name: `${prefix}wfs.version`,
  });
  const outputFormat: string | undefined = useWatch({
    name: `${prefix}wfs.outputFormat`,
  });

  const { capabilities, isLoading } = useWfsCapabilities(url);

  const typeNameOptions = useMemo(() => {
    const available = (capabilities?.featureTypes ?? []).map(featureType => ({
      value: featureType.name,
      label:
        featureType.title && featureType.title !== featureType.name
          ? `${featureType.name} (${featureType.title})`
          : featureType.name,
    }));

    // Il valore già salvato resta selezionabile anche se il servizio non lo
    // espone più (o non risponde): così la config esistente non si perde.
    // Non è invece ammesso inserire manualmente nuovi feature type.
    if (typeName && !available.some(option => option.value === typeName)) {
      return [...available, { value: typeName, label: typeName }];
    }
    return available;
  }, [capabilities, typeName]);

  const hasOptions = typeNameOptions.length > 0;
  const canIntrospect = isIntrospectableLayerUrl(url?.trim());
  // URL valido e interrogazione conclusa senza feature type: il servizio
  // non risponde (o non ne espone). Nessun valore salvato da preservare.
  const isUnavailable = !hasOptions && !isLoading && canIntrospect;

  // Persiste i metadati negoziati con il servizio. L'outputFormat GeoJSON
  // è opportunistico: se il servizio non lo dichiara resta il GML.
  useEffect(() => {
    if (!capabilities) return;

    setValue(`${prefix}wfs.version`, capabilities.version, {
      shouldDirty: true,
    });

    const featureType = capabilities.featureTypes.find(
      candidate => candidate.name === typeName,
    );
    if (!featureType) return;

    const geoJsonFormat = findGeoJsonOutputFormat([
      ...featureType.outputFormats,
      ...capabilities.getFeatureOutputFormats,
    ]);
    setValue(`${prefix}wfs.outputFormat`, geoJsonFormat, {
      shouldDirty: true,
    });
    setValue(`${prefix}wfs.nativeCrs`, featureType.defaultCrs, {
      shouldDirty: true,
    });
  }, [capabilities, typeName, prefix, setValue]);

  return (
    <>
      <FormItem
        label="Feature Type"
        name={`${prefix}wfs.typeName`}
        help={
          hasOptions
            ? 'Seleziona il feature type esposto dal servizio WFS'
            : isLoading
              ? 'Rilevamento dei feature type dal servizio WFS...'
              : canIntrospect
                ? undefined
                : 'Inserisci un URL WFS valido per rilevare i feature type'
        }
      >
        {hasOptions ? (
          <SelectInput
            name={`${prefix}wfs.typeName`}
            options={typeNameOptions}
            showSearch
            loading={isLoading}
            placeholder="Seleziona il feature type..."
          />
        ) : isLoading ? (
          <SelectInput
            name={`${prefix}wfs.typeName`}
            options={[]}
            disabled
            loading
            placeholder="Rilevamento feature type..."
          />
        ) : isUnavailable ? (
          <Typography.Text type="danger">
            Il servizio WFS non risponde o non espone feature type: non è
            possibile configurare questo layer. Verificare l'URL e il tipo di
            servizio.
          </Typography.Text>
        ) : (
          <SelectInput
            name={`${prefix}wfs.typeName`}
            options={[]}
            disabled
            placeholder="Seleziona il feature type..."
          />
        )}
      </FormItem>
      {version && typeName && (
        <Typography.Text type="secondary" style={DETECTED_INFO_STYLE}>
          {`Rilevato dal servizio: WFS ${version} · formato dati ${outputFormat ?? 'GML'}`}
        </Typography.Text>
      )}
    </>
  );
}
